/**
 * Generate sample Excel file for transaction reversal
 * This creates a properly formatted CSV file with demo data
 */

export interface ReversalSampleRow {
  'Original Txn Date': string
  'Txn Rail': '1link' | 'RAAST' | 'RTGS'
  Partner: string
  'Txn Amount': number
  'CBS Reference': string
  'Txn Reference': string
  'Receiver IBAN': string
  'Receiver Name': string
}

export const generateReversalSampleData = (): ReversalSampleRow[] => {
  const today = new Date()
  const formatDate = (daysAgo: number) => {
    const date = new Date(today)
    date.setDate(date.getDate() - daysAgo)
    return date.toISOString().split('T')[0]
  }

  return [
    {
      'Original Txn Date': formatDate(5),
      'Txn Rail': '1link',
      Partner: 'GTBank Ghana',
      'Txn Amount': 850,
      'CBS Reference': 'CBS2024001234',
      'Txn Reference': '123456',
      'Receiver IBAN': 'GB29NWBK60161331926819',
      'Receiver Name': 'Samuel Mensah',
    },
    {
      'Original Txn Date': formatDate(4),
      'Txn Rail': 'RAAST',
      Partner: 'Paga Ghana',
      'Txn Amount': 1200,
      'CBS Reference': 'CBS2024005678',
      'Txn Reference': 'RAAST2024011600001',
      'Receiver IBAN': 'US021000021123456789',
      'Receiver Name': 'David Johnson',
    },
    {
      'Original Txn Date': formatDate(3),
      'Txn Rail': 'RTGS',
      Partner: 'Flutterwave Nigeria',
      'Txn Amount': 2500,
      'CBS Reference': '',
      'Txn Reference': 'RTGS-2024-001-ABC',
      'Receiver IBAN': 'FR7630006000011234567890189',
      'Receiver Name': 'Marie Dupont',
    },
    {
      'Original Txn Date': formatDate(2),
      'Txn Rail': '1link',
      Partner: 'M-Pesa Kenya',
      'Txn Amount': 450,
      'CBS Reference': 'CBS2024009012',
      'Txn Reference': '789012',
      'Receiver IBAN': 'DE89370400440532013000',
      'Receiver Name': 'Klaus Schmidt',
    },
    {
      'Original Txn Date': formatDate(1),
      'Txn Rail': 'RAAST',
      Partner: 'Orange Money Senegal',
      'Txn Amount': 950,
      'CBS Reference': 'CBS2024003456',
      'Txn Reference': 'RAAST2024011700002',
      'Receiver IBAN': 'GB29NWBK60161331926820',
      'Receiver Name': 'Anna Williams',
    },
    {
      'Original Txn Date': formatDate(1),
      'Txn Rail': '1link',
      Partner: 'GTBank Ghana',
      'Txn Amount': 1500,
      'CBS Reference': 'CBS2024007890',
      'Txn Reference': '345678',
      'Receiver IBAN': 'US021000021123456790',
      'Receiver Name': 'Robert Taylor',
    },
  ]
}

export const downloadReversalSampleExcel = () => {
  const data = generateReversalSampleData()

  // Verify all IBANs are exactly 24 characters
  data.forEach((row, index) => {
    if (row['Receiver IBAN'].length !== 24) {
      console.error(
        `Row ${index + 1}: IBAN length is ${row['Receiver IBAN'].length}, expected 24`,
      )
    }
  })

  // Create CSV content with proper escaping
  const headers = [
    'Original Txn Date',
    'Txn Rail',
    'Partner',
    'Txn Amount',
    'CBS Reference',
    'Txn Reference',
    'Receiver IBAN',
    'Receiver Name',
  ]

  const escapeCSV = (value: string | number) => {
    const str = String(value)
    // If value contains comma, quote, or newline, wrap in quotes and escape quotes
    if (str.includes(',') || str.includes('"') || str.includes('\n')) {
      return `"${str.replace(/"/g, '""')}"`
    }
    return str
  }

  const csvRows = [
    headers.join(','),
    ...data.map((row) =>
      [
        row['Original Txn Date'],
        row['Txn Rail'],
        escapeCSV(row['Partner']),
        row['Txn Amount'],
        row['CBS Reference'],
        row['Txn Reference'],
        row['Receiver IBAN'],
        escapeCSV(row['Receiver Name']),
      ].join(','),
    ),
  ]

  const csvContent = csvRows.join('\n')

  // Add UTF-8 BOM for Excel compatibility
  const BOM = '\uFEFF'
  const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' })
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = `transaction_reversal_sample_${new Date().toISOString().split('T')[0]}.csv`
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  window.URL.revokeObjectURL(url)
}
